import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, finalize, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { MetaPixelService } from './meta-pixel.service';
import {
  AssinaturaResponse,
  CheckoutSessionRequest,
  CheckoutSessionResponse,
  CustomerPortalRequest,
  CustomerPortalResponse,
  PlanoTipo,
  UpgradeAnualResponse,
  ReativarAssinaturaResponse,
  DowngradePlanoResponse,
  CancelarAssinaturaRequest,
  CancelarAssinaturaResponse,
  StripeAuditResponse,
  SubscriptionErrorCodes
} from '../models/subscription.model';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;
  private metaPixel = inject(MetaPixelService);

  // Estado reativo principal
  readonly assinatura = signal<AssinaturaResponse | null>(null);
  readonly carregando = signal<boolean>(false);
  readonly processandoCheckout = signal<boolean>(false);

  // Computed signals auxiliares
  readonly podeAcessar = computed(() => this.assinatura()?.podeAcessar ?? true);
  readonly emTrial = computed(() => this.assinatura()?.status === 'trial');
  readonly diasRestantesTrial = computed(() => this.assinatura()?.diasRestantesTrial ?? 0);
  readonly emGracePeriod = computed(() => this.assinatura()?.emGracePeriod ?? false);
  readonly diasRestantesGracePeriod = computed(() => this.assinatura()?.diasRestantesGracePeriod ?? null);
  readonly status = computed(() => this.assinatura()?.status ?? 'trial');
  readonly planoAtual = computed(() => this.assinatura()?.plano ?? null);
  readonly cancelarAoFimDoPeriodo = computed(() => this.assinatura()?.cancelarAoFimDoPeriodo ?? false);
  readonly ehAtivo = computed(() => this.assinatura()?.status === 'active');
  readonly downgradeAgendado = computed(() => this.assinatura()?.downgradeAgendado ?? false);
  readonly planoAgendado = computed(() => this.assinatura()?.planoAgendado ?? null);
  readonly dataEfetivacaoDowngrade = computed(() => this.assinatura()?.dataEfetivacaoDowngrade ?? null);

  /**
   * Obtém a assinatura do usuário autenticado no backend
   */
  carregarMinhaAssinatura(): Observable<AssinaturaResponse> {
    this.carregando.set(true);
    return this.http.get<any>(`${this.apiUrl}/assinaturas/me`).pipe(
      map((res) => ((res && res.data !== undefined) ? res.data : res) as AssinaturaResponse),
      tap((res) => {
        this.assinatura.set(res);
        this._dispararStartTrialSeNovo(res);
      }),
      finalize(() => this.carregando.set(false))
    );
  }

  /**
   * Dispara o evento StartTrial no Meta Pixel uma única vez por usuário.
   * Considera "novo trial" se dataInicioTrial foi criado nos últimos 10 minutos.
   * Usa localStorage para idempotência entre sessões.
   */
  private _dispararStartTrialSeNovo(res: AssinaturaResponse): void {
    if (res.status !== 'trial') return;

    const storageKey = `eclesys_pixel_trial_fired_${res.id}`;
    if (localStorage.getItem(storageKey)) return; // já disparou

    const inicioTrial = new Date(res.dataInicioTrial);
    const agoraMs = Date.now();
    const dezMinutos = 10 * 60 * 1000;

    if (agoraMs - inicioTrial.getTime() <= dezMinutos) {
      // Trial criado nos últimos 10 minutos: é um novo cadastro
      localStorage.setItem(storageKey, '1');
      this.metaPixel.track('StartTrial', { currency: 'BRL', value: 0 });
    }
  }

  /**
   * Atualiza imediatamente o estado reativo local para ativo
   */
  marcarComoAtivo(plano?: string | null, vigenciaFim?: string | null): void {
    const atual = this.assinatura();
    if (atual) {
      this.assinatura.set({
        ...atual,
        status: 'active',
        podeAcessar: true,
        plano: (plano as PlanoTipo) || atual.plano || 'anual',
        vigenciaFim: vigenciaFim || atual.vigenciaFim
      });
    }
    // Sincroniza com o backend em background
    this.carregarMinhaAssinatura().subscribe();
  }

  /**
   * Inicia o fluxo de checkout hospedado do Stripe para o plano selecionado
   */
  iniciarCheckout(
    plano: PlanoTipo,
    dadosFiscais?: { cpfCnpj?: string; razaoSocial?: string },
    successUrl?: string,
    cancelUrl?: string
  ): Observable<CheckoutSessionResponse> {
    this.processandoCheckout.set(true);

    const payload: CheckoutSessionRequest = {
      plano,
      cpfCnpj: dadosFiscais?.cpfCnpj?.trim() || undefined,
      razaoSocial: dadosFiscais?.razaoSocial?.trim() || undefined,
      successUrl: successUrl || `${window.location.origin}/cerimoniais?checkout=success`,
      cancelUrl: cancelUrl || `${window.location.origin}/planos`
    };

    return this.http.post<CheckoutSessionResponse>(`${this.apiUrl}/assinaturas/checkout-session`, payload).pipe(
      tap({
        next: (res) => {
          if (res?.checkoutUrl) {
            window.location.href = res.checkoutUrl;
          }
        },
        error: (err) => {
          const errorCode = err?.error?.errorCode;
          if (
            errorCode === SubscriptionErrorCodes.ALREADY_SUBSCRIBED_TO_THIS_PLAN ||
            errorCode === SubscriptionErrorCodes.PLAN_MIGRATION_REQUIRED
          ) {
            this.carregarMinhaAssinatura().subscribe();
          }
        }
      }),
      finalize(() => this.processandoCheckout.set(false))
    );
  }

  /**
   * Gera a sessão do Stripe Customer Portal e redireciona o usuário
   */
  abrirPortal(returnUrl?: string): Observable<CustomerPortalResponse> {
    const payload: CustomerPortalRequest = {
      returnUrl: returnUrl || `${window.location.origin}/cerimoniais`
    };

    return this.http.post<CustomerPortalResponse>(`${this.apiUrl}/assinaturas/portal-session`, payload).pipe(
      tap((res) => {
        if (res?.portalUrl) {
          window.location.href = res.portalUrl;
        }
      })
    );
  }

  /**
   * Realiza o upgrade imediato de Mensal para Anual com cálculo proporcional (proration)
   */
  upgradeAnual(): Observable<UpgradeAnualResponse> {
    return this.http.post<UpgradeAnualResponse>(`${this.apiUrl}/assinaturas/upgrade-anual`, {}).pipe(
      tap(() => {
        this.carregarMinhaAssinatura().subscribe();
      })
    );
  }

  /**
   * Reativa a assinatura cancelada para continuar renovando automaticamente
   */
  reativar(): Observable<ReativarAssinaturaResponse> {
    return this.http.post<ReativarAssinaturaResponse>(`${this.apiUrl}/assinaturas/reativar`, {}).pipe(
      tap(() => {
        this.carregarMinhaAssinatura().subscribe();
      })
    );
  }

  /**
   * Programa o downgrade da assinatura para o plano mensal no término do ciclo vigente
   */
  downgradeMensal(): Observable<DowngradePlanoResponse> {
    return this.http.post<DowngradePlanoResponse>(`${this.apiUrl}/assinaturas/downgrade-mensal`, {}).pipe(
      tap(() => {
        this.carregarMinhaAssinatura().subscribe();
      })
    );
  }

  /**
   * Agenda o cancelamento da assinatura ao fim do período faturado
   */
  cancelarAgendado(motivo?: string): Observable<CancelarAssinaturaResponse> {
    const payload: CancelarAssinaturaRequest = { motivo };
    return this.http.post<CancelarAssinaturaResponse>(`${this.apiUrl}/assinaturas/cancelar-agendado`, payload).pipe(
      tap(() => {
        this.carregarMinhaAssinatura().subscribe();
      })
    );
  }


  /**
   * Realiza a auditoria técnica do ambiente Stripe
   */
  carregarDiagnostico(): Observable<StripeAuditResponse> {
    return this.http.get<StripeAuditResponse>(`${this.apiUrl}/assinaturas/diagnostico`);
  }
}
